import type { FetchData } from '@hooks/useDataFetcher';
import { getFinanceKey } from '@shared/helpers';
import { useCallback, useState } from 'react';
import type {
  admin_financeadmin_Finance,
  admin_financeadmin_Template,
  financeadmin_FinanceResponse,
  financeadmin_ManyFinancesResponse,
  financeadmin_TemplatesResponse,
} from 'types/offers';

interface IState {
  loading: boolean;
  templates: admin_financeadmin_Template[];
  finances: Map<string, admin_financeadmin_Finance[]>;
}

export type UseFinances = IState & {
  getFinanceTemplates: () => Promise<admin_financeadmin_Template[]>;
  createFinance: (
    market: string,
    finance: admin_financeadmin_Finance,
  ) => Promise<admin_financeadmin_Finance | undefined>;
  updateFinance: (
    market: string,
    finance: admin_financeadmin_Finance,
  ) => Promise<admin_financeadmin_Finance | undefined>;
  getFinancesByMarket: (
    market: string,
  ) => Promise<admin_financeadmin_Finance[]>;
  updateFinancesStatus: (
    market: string,
    finances: admin_financeadmin_Finance[],
    status: 'ACTIVE' | 'INACTIVE',
  ) => Promise<void>;
};

export const useFinances = (fetchData: FetchData): UseFinances => {
  const [state, setState] = useState<IState>({
    templates: [],
    loading: false,
    finances: new Map(),
  });

  const getFinanceTemplates = useCallback<
    UseFinances['getFinanceTemplates']
  >(async () => {
    if (state.templates.length) {
      return state.templates;
    }

    setState((state) => ({ ...state, loading: true }));

    const res = await fetchData<financeadmin_TemplatesResponse>(
      '/api/offers/v1/admin/finances/templates',
    );

    setState((state) => ({
      ...state,
      loading: false,
      templates: res.data?.templates?.length
        ? res.data.templates
        : state.templates,
    }));

    return res.data?.templates ?? [];
  }, [state.templates, fetchData]);

  const createFinance = useCallback<UseFinances['createFinance']>(
    async (market, finance) => {
      setState((state) => ({ ...state, loading: true }));

      const res = await fetchData<financeadmin_FinanceResponse>(
        `/api/offers/v1/admin/markets/${market}/finances`,
        undefined,
        'POST',
        { ...finance, name: finance.name || getFinanceKey(finance) },
      );

      setState((state) => ({
        ...state,
        loading: false,
        finances: res.data?.finance
          ? new Map(state.finances).set(market, [
              res.data.finance,
              ...(state.finances.get(market) ?? []),
            ])
          : state.finances,
      }));

      return res.data?.finance;
    },
    [fetchData],
  );

  const updateFinance = useCallback<UseFinances['updateFinance']>(
    async (market, finance) => {
      setState((state) => ({ ...state, loading: true }));

      const res = await fetchData<financeadmin_FinanceResponse>(
        `/api/offers/v1/admin/finances/${finance.id}`,
        undefined,
        'PUT',
        {
          name: finance.name,
          terms: finance.terms && {
            mileage: finance.terms.mileage && {
              choices: finance.terms?.mileage?.choices,
              defaultChoice: finance.terms?.mileage?.defaultChoice,
            },
            contractLength: finance.terms.contractLength && {
              choices: finance.terms?.contractLength?.choices,
              defaultChoice: finance.terms?.contractLength?.defaultChoice,
            },
          },
        },
      );

      setState((state) => ({
        ...state,
        loading: false,
        finances: res.data?.finance?.id
          ? new Map(state.finances).set(
              market,
              (state.finances.get(market) ?? []).map((f) =>
                f.id === finance.id ? res.data!.finance! : f,
              ),
            )
          : state.finances,
      }));

      return res.data?.finance;
    },
    [fetchData],
  );

  const getFinancesByMarket = useCallback<UseFinances['getFinancesByMarket']>(
    async (market) => {
      if (state.finances.has(market)) {
        return state.finances.get(market)!;
      }

      setState((state) => ({ ...state, loading: true }));

      const res = await fetchData<financeadmin_ManyFinancesResponse>(
        `/api/offers/v1/admin/markets/${market}/finances`,
      );

      setState((state) => ({
        ...state,
        loading: false,
        finances: res.data?.finances?.length
          ? new Map(state.finances).set(
              market,
              res.data.finances.sort((a, b) =>
                (a.status ?? '').localeCompare(b.status ?? ''),
              ),
            )
          : state.finances,
      }));

      return (
        res.data?.finances?.sort((a, b) =>
          (a.status ?? '').localeCompare(b.status ?? ''),
        ) ?? []
      );
    },
    [state.finances, fetchData],
  );

  const updateFinancesStatus = useCallback<UseFinances['updateFinancesStatus']>(
    async (market, finances, status) => {
      setState((state) => ({ ...state, loading: true }));

      const results = await Promise.allSettled(
        finances.map((finance) =>
          fetchData(
            status === 'INACTIVE'
              ? `/api/offers/v1/admin/finances/${finance.id}`
              : `/api/offers/v1/admin/finances/${finance.id}/activate`,
            undefined,
            status === 'INACTIVE' ? 'DELETE' : 'POST',
          )
            .then(() => ({ id: finance.id, success: true }))
            .catch((err) => ({ id: finance.id, success: false, error: err })),
        ),
      );

      const successfulIds = results
        .filter(
          (r): r is PromiseFulfilledResult<{ id: string; success: true }> =>
            r.status === 'fulfilled' && r.value.success,
        )
        .map((r) => r.value.id);

      const failedCount = finances.length - successfulIds.length;

      const financeIds = new Set(successfulIds);

      setState((state) => ({
        ...state,
        loading: false,
        finances: !state.finances.has(market)
          ? state.finances
          : new Map(state.finances).set(
              market,
              state.finances
                .get(market)!
                .map((finance) =>
                  financeIds.has(finance.id!)
                    ? { ...finance, status }
                    : finance,
                ),
            ),
      }));

      if (failedCount > 0) {
        throw new Error(
          `${failedCount} of ${finances.length} operations failed`,
        );
      }
    },
    [fetchData],
  );

  return {
    ...state,
    createFinance,
    updateFinance,
    getFinancesByMarket,
    getFinanceTemplates,
    updateFinancesStatus,
  };
};
